// Reject order action with modal
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DocumentType, DocumentTypeWithVirtual, MessageV2DTO, TransactionState } from 'support/types';
import { useRejectOrder } from 'services/repositories/transactions/transactions';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { logError } from 'services/logging/logging';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { useContainsSearchQueryAction } from './helpers';
import { DialogTitle } from '@headlessui/react';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Radio } from 'components/Form/Radio/Radio';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';
import { Input } from 'components/Form/Input/Input/Input';
import { useEffect } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EMPTY_FUNCTION } from 'support/helpers/const/const';
import { ActionConfig } from '../components/types';
import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';

// Custom hook for creating reject order action with modal
const REJECT_ORDER_URL_ACTION = 'reject_order';
export const useRejectOrderAction = (
  message: MessageV2DTO,
  documentType: DocumentType,
  relatedTransactions: Array<MessageV2DTO> | undefined,
  orderResponseActionTest: ActionConfig['test'],
): ActionConfig => {
  const openModalByDefault = useContainsSearchQueryAction(REJECT_ORDER_URL_ACTION);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isRejectEntireOrderModalOpen, setIsRejectEntireOrderModalOpen] = useState(openModalByDefault);
  const { mutate: rejectOrder, isLoading: isRejecting } = useRejectOrder();

  const handleRejectOrder = ({ reason }: { reason: string }) => {
    rejectOrder(
      { messageId: message.id, reason },
      {
        onSuccess: ({ transactionId, transactionType }) => {
          setIsRejectEntireOrderModalOpen(false);
          navigate(routeToTransactionPage(transactionType, transactionId));
        },
        onError: (e) => {
          logError(e);
          addNotification(
            {
              title: t('webedi:notifications.rejectOrderFailed.title'),
              subtitle: t('webedi:notifications.rejectOrderFailed.subtitle'),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  const hasOrderResponse = () => {
    return relatedTransactions?.some((message: MessageV2DTO) => {
      const nonVirtualDocumentType = mapVirtualToActualDocumentType(message.type as DocumentTypeWithVirtual);
      return (
        nonVirtualDocumentType === DocumentType.orderResponse &&
        [TransactionState.success, TransactionState.pending].includes(message.state as TransactionState)
      );
    });
  };

  return {
    label: t('transactions:show.actionButtons.rejectOrder'),
    type: 'destructive',
    onClick: () => setIsRejectEntireOrderModalOpen(true),
    analyticsId: 'transactions:reject_order',
    documentType,
    test: (message) => {
      const hasOrderResponseActionTest = orderResponseActionTest ?? (() => true);
      return hasOrderResponseActionTest(message) && !hasOrderResponse();
    },
    renderModal: () => (
      <RejectEntireOrderModal
        isLoading={isRejecting}
        open={isRejectEntireOrderModalOpen}
        setOpen={setIsRejectEntireOrderModalOpen}
        onReject={handleRejectOrder}
      />
    ),
  };
};

// RejectEntireOrderModal form values type
type FormValues = {
  reason: string;
  otherReason?: string;
};

function RejectEntireOrderModal({
  open,
  setOpen,
  onReject,
  isLoading,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onReject: ({ reason }: { reason: string }) => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const rejectionReasons = [
    {
      value: 'transactions:reject_entire.modal.reasons.out_of_stock',
      label: t('transactions:reject_entire.modal.reasons.out_of_stock'),
    },
    {
      value: 'transactions:reject_entire.modal.reasons.discontinued',
      label: t('transactions:reject_entire.modal.reasons.discontinued'),
    },
    {
      value: 'transactions:reject_entire.modal.reasons.duplicated_order',
      label: t('transactions:reject_entire.modal.reasons.duplicated_order'),
    },
    {
      value: 'transactions:reject_entire.modal.reasons.other',
      label: t('transactions:reject_entire.modal.reasons.other'),
    },
  ];

  const otherReasonValue = rejectionReasons.at(-1)?.value;

  // Create schema with conditional validation
  const schema = yup.object().shape({
    reason: yup.string().required(t('transactions:reject_entire.modal.errors.reason_required')),
    otherReason: yup.string().when('reason', {
      is: (value: string) => value === otherReasonValue,
      then: (schema) => schema.required(t('transactions:reject_entire.modal.errors.other_reason_required')),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      reason: '',
      otherReason: '',
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const isOtherReason =
    useWatch({
      control,
      name: 'reason',
    }) === otherReasonValue;

  const onSubmit = (data: yup.InferType<typeof schema>) => {
    if (data.reason === otherReasonValue) {
      onReject({ reason: data.otherReason! });
    } else {
      onReject({ reason: t(data.reason) });
    }
  };

  // Reset form when modal is opened/closed
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  return (
    <BasePopup open={open} setOpen={isLoading ? EMPTY_FUNCTION : setOpen}>
      <div className="p-6">
        <DialogTitle as="h3" className="text-lg font-medium text-gray-900">
          {t('transactions:reject_entire.modal.title')}
        </DialogTitle>
        <p className="mt-2 text-sm text-gray-500">{t('transactions:reject_entire.modal.description')}</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <div className="mt-4">
                <Radio.Group>
                  {rejectionReasons.map(({ value, label }) => (
                    <Radio.Item
                      key={value}
                      label={label}
                      value={value}
                      onChange={() => field.onChange(value)}
                      checked={field.value === value}
                      hasErrors={!!errors.reason}
                    />
                  ))}
                </Radio.Group>
                {errors.reason && <span className="mt-2 text-sm text-red-600">{errors.reason.message}</span>}
              </div>
            )}
          />

          {isOtherReason && (
            <div className="ml-6 mt-3">
              <Controller
                name="otherReason"
                control={control}
                render={({ field }) => (
                  <Input
                    id="otherReason"
                    {...field}
                    placeholder={t('transactions:reject_entire.modal.reasons.other_placeholder')}
                    errors={errors.otherReason}
                  />
                )}
              />
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              analyticsId="close_proof_of_transmission"
              variant="secondary"
              disabled={isLoading}
              onClick={() => setOpen(false)}
              type="button"
            >
              <Translate i18nKey="common:close" />
            </Button>
            <Button analyticsId="reject_entire_order" variant="secondary-danger" type="submit" loading={isLoading}>
              <Translate i18nKey="transactions:reject_entire.modal.cta" />
            </Button>
          </div>
        </form>
      </div>
    </BasePopup>
  );
}
